import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { Params } from '../../../../node_modules/@angular/router';
import { BackendService } from '../../core/backend.service';
import { ApiResult } from '../../core/messages/api-result';
import { ApiFilteredItem } from '../../viewmodels/filters/api-filtered-item';
import { DateTimeFormatterPipe } from '../pipes/date-time-formatter.pipe';
import { DynamicDetailsListItemAdapter } from './dynamic-details-list-item';
import { ApiFilterAttribute, FilterAttribute, FilterAttributeType, LocalFilterAttribute } from './filter-attribute';
import { FilterAttributeFactoryService } from './filter-attribute-factory.service';

@Injectable()
export class DynamicDetailsListFilterService {
  public filterApplied: Subject<FilterAttribute[]> = new Subject<FilterAttribute[]>();
  public filterAttributes = new Array<FilterAttribute>();
  public filterAttributesConfigured = new Subject();
  public appliedFilterAttributes = new Array<FilterAttribute>();

  public filterText: string;
  public attributeMatched: boolean;
  public errors = new Subject<ApiResult>();
  public messages: Subject<any> = new Subject<any>();

  private separator = ':';
  private matchedAttribute: FilterAttribute;
  private filterSearchString: string;

  constructor(
    private backendService: BackendService,
    private dateTimeFormatter: DateTimeFormatterPipe,
    private filterAttributeFactory: FilterAttributeFactoryService,
  ) { }

  public matchProperty(): string {
    if (!this.filterText || this.filterAttributes.length < 1) {
      return null;
    }
    const index = this.filterAttributes.findIndex(att => att.displayName.startsWith(this.filterText));
    return index > -1 ? this.filterAttributes[index].propertyName : null;
  }

  public matchAttribute(): string {
    if (!this.filterText || this.filterAttributes.length < 1) {
      this.attributeMatched = false;
      return null;
    }

    const separatorIndex = this.filterText.indexOf(this.separator);
    const textToMatch = separatorIndex > -1 ? this.filterText.substring(0, separatorIndex) : this.filterText;
    const attributeIndex = this.filterAttributes.findIndex(att => att.displayName.toLowerCase().startsWith(textToMatch.toLowerCase()));
    this.attributeMatched = attributeIndex > -1 && separatorIndex > -1;

    if (this.attributeMatched) {
      this.matchedAttribute = this.filterAttributes[attributeIndex];
      this.filterSearchString = this.filterText.substring(separatorIndex + 1).trim();
    }

    return attributeIndex > -1 ? separatorIndex > -1 ? this.filterAttributes[attributeIndex].displayName + this.separator : this.filterAttributes[attributeIndex].displayName.substring(textToMatch.length) : null;
  }

  public applyFilter() {
    if (this.matchedAttribute && this.filterSearchString) {

      if (this.matchedAttribute instanceof ApiFilterAttribute) {
        this.applyApiFilter(this.filterSearchString, this.matchedAttribute.apiRoute);
      } else {
        const filterAttribute = new LocalFilterAttribute();
        filterAttribute.propertyName = this.matchedAttribute.propertyName;
        filterAttribute.displayName = this.matchedAttribute.displayName;
        filterAttribute.textToMatch = this.filterSearchString;
        filterAttribute.attributeType = this.matchedAttribute.attributeType;

        this.appliedFilterAttributes.push(filterAttribute);
        this.filterApplied.next(this.appliedFilterAttributes);

        this.resetMatchedFilter();
      }
    }
  }

  public applyFilterParams(filters: Params) {
    for (const param in filters) {
      if (filters.hasOwnProperty(param)) {
        this.filterText = param + ':' + filters[param];
        this.matchAttribute();
        if (this.attributeMatched) {
          this.applyFilter();
        }
      }
    }
  }

  public addLocalFilterAttribute(propertyName: string, displayName: string, type: FilterAttributeType = FilterAttributeType.String) {
    this.filterAttributes.push(this.filterAttributeFactory.createLocalFilterAttribute(propertyName, displayName, type));
  }

  public addApiFilterAttribute(propertyName: string, displayName: string, apiRoute: string, type: FilterAttributeType = FilterAttributeType.String) {
    this.filterAttributes.push(this.filterAttributeFactory.createApiFilterAttribute(propertyName, displayName, apiRoute, type));
  }

  public filter(items: DynamicDetailsListItemAdapter[], itemIdProperyName: string, filters: FilterAttribute[]): DynamicDetailsListItemAdapter[] {
    return _.filter(items, item => {
      let matched = true;
      filters.forEach(filter => {
        if (filter instanceof LocalFilterAttribute) {
          const propertyToFilterOn = _.get(item.detailsItem, filter.propertyName);
          if (propertyToFilterOn !== null) {
            switch (filter.attributeType) {
            case FilterAttributeType.Date:
              if (this.dateTimeFormatter.transform(propertyToFilterOn) !== filter.textToMatch) {
                matched = false;
              }
              break;
            default:
              if (String(propertyToFilterOn).toLowerCase().trim() !== filter.textToMatch.toLowerCase().trim()) {
                matched = false;
              }
              break;
            }
          } else {
            matched = false;
          }
        } else if (filter instanceof ApiFilterAttribute) {
          let itemFound = false;
          let index = 0;
          do {
            const filterItem = filter.filterResult[index];
            const itemId = item.detailsItem[itemIdProperyName];
            itemFound = (filterItem && itemId) && filterItem.itemId === item.detailsItem[itemIdProperyName];
            index++;
          } while (!itemFound && index < filter.filterResult.length);
          if (!itemFound) {
            matched = false;
          }
        } else {
          matched = false;
        }
      });
      return matched;
    });
  }

  private applyApiFilter(searchString: string, apiRoute: string) {

    const apiAttribute = new ApiFilterAttribute();

    apiAttribute.propertyName = this.matchedAttribute.propertyName;
    apiAttribute.displayName = this.matchedAttribute.displayName;
    apiAttribute.textToMatch = this.filterSearchString;

    this.backendService.getAllByIdAsync(ApiFilteredItem, searchString, apiRoute)
      .then(data => {
        apiAttribute.filterResult = data;
        this.appliedFilterAttributes.push(apiAttribute);
        this.filterApplied.next(this.appliedFilterAttributes);
        this.resetMatchedFilter();
      }, (errorResult: ApiResult) => {
        if (errorResult) {
          this.errors.next(errorResult);
        }
      });
  }

  private resetMatchedFilter() {
    this.attributeMatched = false;
    this.matchedAttribute = null;
    this.filterSearchString = '';
    this.filterText = '';
  }

  public clearFilters() {
    this.resetMatchedFilter();
    this.appliedFilterAttributes = [];
  }

  public removeFilter(filterItem: FilterAttribute) {
    const itemIndex = this.appliedFilterAttributes.indexOf(filterItem);
    if (itemIndex > -1) {
      this.appliedFilterAttributes.splice(itemIndex, 1);
      this.filterApplied.next(this.appliedFilterAttributes);
    }
  }

  public getDisplayName(propertyName: string) {
    const index = this.filterAttributes.findIndex(att => att.propertyName === propertyName);
    return index > -1 ? this.filterAttributes[index].displayName : null;
  }
}
